import { useMemo } from 'react';

import type { DomainQueryOptions } from '@/domain/shared/types';
import { useBehaviorController_getTilt } from '@/services/hooks';
import type { TiltResponseDto } from '@/services/types';

import { mapTiltResponseToDomain } from './mappers';
import { toBehaviorRangeQuery } from './queryParams';
import type { BehaviorDateRangeParams, UseGetTiltResult } from './types';

const EMPTY_TILT_DATA: TiltResponseDto = {
    data: [],
    lastMeasurement: '',
};

/**
 * Domain hook to fetch tilt data for a cage
 * Returns tilt data points including temperature and depth
 * for the specified date range
 *
 * @param cageId - The cage ID to fetch tilt data for
 * @param params - Optional date range parameters (from/to in YYYY-MM-DD format)
 * @param options - Optional React Query options (e.g. enabled, refetchInterval)
 * @returns Query result with mapped tilt chart data
 */
export const useGetTilt = (
    cageId: number,
    params?: BehaviorDateRangeParams,
    options?: DomainQueryOptions<TiltResponseDto>
): UseGetTiltResult => {
    const tiltQuery = useBehaviorController_getTilt(cageId, toBehaviorRangeQuery(params), options);

    const tiltData = useMemo(
        () => mapTiltResponseToDomain(tiltQuery.data?.data ?? EMPTY_TILT_DATA),
        [tiltQuery.data?.data]
    );

    return {
        ...tiltQuery,
        isLoading: tiltQuery.isPending,
        data: tiltData,
    };
};
